import { Fragment, useCallback, useMemo, useState } from 'react';
import Link from 'next/link';

import Button from '@/components/Button';
import Header from '@/components/Header';
import Icon from '@/components/Icon';

type ValidateFullCppProps = {
	exportCppPdf: () => {},
	validationResults: any[];
	onCancel: any;
	onConfirm: any;
};

export default function ValidateFullCpp({
	exportCppPdf,
	validationResults,
	onCancel
}: ValidateFullCppProps ) {
	const [ isPrinting, setIsPrinting ] = useState( false );

	const canPrint = useMemo( () => {
		return validationResults.every( result => result.formIssues.length === 0 && !result.hasFormsInDraftStatus && !result.formIsEmpty );
	}, [ validationResults ] );

	const handlePrintClick = useCallback( () => {
		setIsPrinting( true );
		exportCppPdf();
	}, [ exportCppPdf, setIsPrinting ] );

	return (
		<div>
			<Header element="h2" variant="h3">Validation Results</Header>

			<div className="mt-4">
				<div className="max-h-[250px] overflow-auto">
					<table className="w-full mb-4">
						<thead>
							<tr>
								<th className="border-b border-b-rural-gray-500 p-1 text-xs text-left"></th>
								<th className="border-b border-b-rural-gray-500 p-1 text-xs text-left">Form</th>
								<th className="border-b border-b-rural-gray-500 p-1 text-xs text-left">Field Name</th>
								<th className="border-b border-b-rural-gray-500 p-1 text-xs text-left">Validation message</th>
							</tr>
						</thead>

						<tbody>
							{ validationResults.map( ( result: any, resultIndex: number ) => (
								<Fragment key={ result.formTitle }>
									<tr className={ resultIndex % 2 ? '' : 'bg-slate-100' }>
										<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left">
											{ ( result.formIssues.length === 0 && !result.hasFormsInDraftStatus && !result.formIsEmpty ) ? (
												<Icon glyph="check" color="#0baa5c" />
											) : (
												<Icon glyph="warning" color="#c0b300" />
											)}
										</td>
										<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left">
											<Link
												className="underline font-bold"
												href={ `/cpp/form?cppId=${ result.cppId }&formId=${ result.formId }` }
												onClick={ onCancel }
											>
												{ result.formTitle }
											</Link>
										</td>
										<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left"></td>
										<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left">
											{ result.hasFormsInDraftStatus && (
												<>This form or one its subcoverages is saved as a draft. Please review.</>
											)}

											{ result.formIsEmpty && (
												<>This form has no data. Please review.</>
											)}
										</td>
									</tr>

									{ result.formIssues.map( ( issue: any, index: number ) => (
										<tr key={ `${ result.formTitle }.${ index }` } className={ resultIndex % 2 ? '' : 'bg-slate-100' }>
											<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left"></td>
											<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left">
												{ issue.formTitle }
											</td>
											<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left">{ issue.questionId }</td>
											<td className="border-b border-b-rural-gray-500 p-1 text-xs text-left">{ issue.message }</td>
										</tr>
									))}
								</Fragment>
							))}
						</tbody>
					</table>
				</div>
			</div>

			<div className="flex gap-x-4 mt-4">
				{ canPrint && (
					<Button type="button" variant="primary" size="medium" disabled={ isPrinting } onClick={ () => handlePrintClick() }>Export to PDF</Button>
				)}

				<Button type="button" variant="outline" size="medium" onClick={ onCancel }>Close</Button>
			</div>
		</div>
	);
}
